import {
  isSameDay,
  startOfDay,
  isWeekend,
  getYear,
  eachDayOfInterval,
  formatISO,
  differenceInBusinessDays,
  differenceInDays,
  isValid,
  isWithinInterval,
  eachWeekendOfYear,
} from 'date-fns';

import { ICalendarService } from './calendar.interface';

export class Calendar implements ICalendarService {
  private holidaysAndWeekends: Record<number, Record<string, boolean>> = {};

  private holidays: Record<number, Record<string, boolean>> = {};

  /**
   * Загрузить календарь выходных и праздничных дней
   * @param {Date} [date] Дата, по которой будет вычисляться год для загружаемого календаря. Опционально
   * @param {string} [id] Id сотрудника необходим, если загружается календарь для конкретного сотрудника. Опционально
   * @returns {Promise<void>}
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async download(date = new Date(), id?: string) {
    const year = getYear(date);
    const defaultYear = 2021;

    if (year < defaultYear) return;

    this.holidaysAndWeekends[year] = eachWeekendOfYear(date).reduce(
      (acc, day) => ({
        ...acc,
        [formatISO(day, { representation: 'date' })]: true,
      }),
      {},
    );
  }

  /** Преобразовать дату в формат ISO
   * @param {Date} date Дата, которая должна быть преобразована
   * @param {Object} [options] Параметры для преобразования к ISO
   * @param {'complete' | 'date' | 'time' } options.representation Параметры для преобразования к ISO
   * @returns {string}
   */
  formatISO(
    date: Date,
    options: { representation: 'complete' | 'date' | 'time' } = {
      representation: 'complete',
    },
  ) {
    return formatISO(date, options);
  }

  /** Определить является выбранная дата выходным или праздничным днем
   * на основе ранее загруженных календарей
   * @param {Date} date Выбранная дата
   * @returns {boolean}
   */
  isHolidayOrWeekend(date: Date): boolean {
    const year = getYear(date);

    if (this.holidaysAndWeekends[year]) {
      return !!this.holidaysAndWeekends[year][
        formatISO(date, { representation: 'date' })
      ];
    }

    return isWeekend(date);
  }

  /** Определить является выбранная дата праздничным днем
   * на основе ранее загруженных календарей
   * @param {Date} date
   * @returns {boolean}
   */
  isHoliday(date: Date): boolean {
    const year = getYear(date);

    if (this.holidays[year]) {
      return !!this.holidays[year][formatISO(date, { representation: 'date' })];
    }

    return false;
  }

  /** Определить совпадает год, месяц и день у двух выбранных дат
   * @param {Date} date1 Дата №1
   * @param {Date} date2 Дата №2
   * @returns {boolean}
   */
  isSameDay(date1: Date, date2: Date): boolean {
    return isSameDay(date1, date2);
  }

  /** Определить количество полных дней между двумя датами
   * Дробные дни округляются до нуля
   * @param {Date} date1 Дата №1
   * @param {Date} date2 Дата №2
   * @returns {number}
   */
  differenceInDays(
    date1: Date,
    date2: Date,
    options?: { excludeHolidays?: boolean; excludeWorkingDays?: boolean },
  ): number {
    if (options?.excludeHolidays) {
      return (
        differenceInDays(date1, date2) -
        this.countOfHolidaysInInterval(date2, date1)
      );
    }

    if (options?.excludeWorkingDays) {
      return differenceInBusinessDays(date1, date2);
    }

    return differenceInDays(date1, date2);
  }

  /** Получить дату начала выбранного дня
   * Результат будет в локальной временной зоне
   * @param {Date} date Выбранная дата
   * @returns {Date}
   */
  startOfDay(date: Date): Date {
    return startOfDay(date);
  }

  /** Входит ли дата в один из интервалов
   * @param {Date} date Выбранная дата
   * @param {[start: Date, end: Date]} intervals Массив интервалов
   * @returns {Date}
   * */
  isWithinIntervals(date: Date, intervals: [start: Date, end: Date][]) {
    return intervals.some((interval) =>
      isWithinInterval(date, { start: interval[0], end: interval[1] }),
    );
  }

  /** Определить количество праздничных дней между двумя датами
   * на основе ранее загруженных календарей
   * @params {Date} date1 Дата №1
   * @params {Date} date2 Дата №2
   * @returns {number}
   * */
  countOfHolidaysInInterval(date1: Date, date2: Date) {
    return eachDayOfInterval({
      start: date1 < date2 ? date1 : date2,
      end: date1 < date2 ? date2 : date1,
    }).filter((day) => this.isHoliday(day)).length;
  }

  /** Определить является выбранная дата валидной
   * @params {Date|null|string} date Выбранная дата
   * @returns {boolean}
   * */
  isValidDate(date: string | null | Date) {
    return isValid(date);
  }
}
